import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Pencil, StickyNote } from "lucide-react";
import { Button } from "../Button";
import { ExpandableNote } from "../ExpandableNote";
import { IconButton } from "../IconButton";
import { truncate } from "../../utils/format";

/**
 * How the popover commits what was typed. Either way the text lives inside the popover while it is open, so a keystroke
 * re-renders the textarea and nothing else; the row, and whatever else reads the note, updates once.
 * - "save": the note belongs to a rating already on the member, so it is a round trip to the API. The popover keeps a
 *   draft until Save, Cancel discards it, and a failed save leaves the text in place.
 * - "close": the note belongs to a rating being picked, part of a draft nothing saves until Add to Member. The popover
 *   hands the text back when it closes, however it closes, so there is nothing to save or cancel.
 */
type NoteCommit = { commit: "save"; onSave: (notes: string | null) => Promise<boolean> } | { commit: "close"; onChange: (notes: string) => void };

type NoteEditorProps = NoteCommit & {
  notes: string | null;
  /** What the note belongs to, for accessible names ("notes for Type II, poorly controlled"). */
  label: string;
  disabled?: boolean;
  /** How the note reads in the cell: the member grid gives it room with View more; the picker keeps a fixed 30-character slot so 300 rows never reflow. */
  preview?: "expandable" | "short";
  children?: never;
};

interface PanelPosition {
  top: number;
  left: number;
  width: number;
}

const PANEL_WIDTH = 440;
const PANEL_HEIGHT_ESTIMATE = 240;
const GAP = 6;
const VIEWPORT_MARGIN = 8;
/** Not a limit, a nudge: underwriter notes run long, but a pasted report should be noticed. */
const LONG_NOTE_CHARACTERS = 2000;
export const SHORT_NOTE_PREVIEW_CHARACTERS = 30;

function positionNear(anchor: DOMRect): PanelPosition {
  const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, window.innerWidth - width - VIEWPORT_MARGIN));
  const fitsBelow = anchor.bottom + GAP + PANEL_HEIGHT_ESTIMATE < window.innerHeight;
  const top = fitsBelow ? anchor.bottom + GAP : anchor.top - GAP - PANEL_HEIGHT_ESTIMATE;
  return { top: Math.max(VIEWPORT_MARGIN, top), left, width };
}

/**
 * The note cell of a rating row: the text, plus a pencil that opens a popover editor anchored to the row. The editor is a
 * portal, so the grid never reflows or clips while someone writes a paragraph, and it follows its row while the page
 * scrolls. Escape closes it, Ctrl+Enter commits, and a click elsewhere closes an editor with nothing unsaved; one with
 * unsaved text stays open until Save or Cancel, so a long note is never lost to a stray click.
 */
export function NoteEditor(props: NoteEditorProps) {
  const { notes, label, disabled = false, preview = "expandable" } = props;
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const [draft, setDraft] = useState(notes ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const isOpen = position !== null;
  const hasChanges = draft.trim() !== (notes ?? "");
  // Only a saved note can be left unsaved; a "close" note goes back to its owner the moment the popover closes.
  const isDirty = props.commit === "save" && hasChanges;
  const title = `${notes ? "Edit" : "Add"} ${label}`;

  const close = () => {
    if (props.commit === "close" && hasChanges) props.onChange(draft);
    setPosition(null);
    anchorRef.current?.focus();
  };
  // The outside-click listener is subscribed once per open, but must close with the current draft, not the one it was created with.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });

  useEffect(() => {
    if (isOpen) textareaRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isDirty) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      closeRef.current();
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [isOpen, isDirty]);

  useEffect(() => {
    if (!isOpen) return;
    const follow = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setPosition(positionNear(rect));
    };
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [isOpen]);

  const open = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDraft(notes ?? "");
    setPosition(positionNear(rect));
  };

  const commit = async () => {
    if (props.commit === "close" || !isDirty) {
      close();
      return;
    }
    setIsSaving(true);
    const trimmed = draft.trim();
    const saved = await props.onSave(trimmed === "" ? null : trimmed);
    setIsSaving(false);
    if (saved) close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      void commit();
    }
  };

  return (
    <span className="flex min-w-0 items-start gap-1">
      {!notes ? (
        // An empty cell says what the icon does (Brady, 2026-09-07); once a note exists the pencil alone reads fine.
        <button ref={anchorRef} type="button" aria-label={title} title={title} disabled={disabled} onClick={open} className="inline-flex min-h-[32px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-green-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent">
          Add Note
          <StickyNote size={16} aria-hidden="true" />
        </button>
      ) : (
        <>
          <NotePreview notes={notes} label={label} preview={preview} />
          <IconButton ref={anchorRef} icon={Pencil} label={title} size="sm" disabled={disabled} onClick={open} className="shrink-0" />
        </>
      )}
      {position &&
        createPortal(
          <div ref={panelRef} role="dialog" aria-modal="false" aria-labelledby={headingId} className="fixed z-[60] rounded-xl border border-gray-200 bg-white p-3 shadow-xl" style={{ top: position.top, left: position.left, width: position.width }} onKeyDown={handleKeyDown}>
            <p id={headingId} className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {title}
            </p>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
              rows={Math.min(12, Math.max(4, draft.split("\n").length + 1))}
              aria-label={label}
              disabled={isSaving}
              className="block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed text-gray-800 focus:border-brand-green focus:outline-none focus:ring-2 focus:ring-brand-green-50"
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500" aria-live="polite">
                {draft.length > LONG_NOTE_CHARACTERS ? `${draft.length.toLocaleString()} characters` : props.commit === "save" ? "Ctrl+Enter saves, Esc cancels" : "Esc or a click away closes"}
              </span>
              {props.commit === "save" ? (
                <span className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={close} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => void commit()} disabled={isSaving || !isDirty}>
                    Save
                  </Button>
                </span>
              ) : (
                <Button variant="secondary" size="sm" onClick={close}>
                  Done
                </Button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}

/** The note as it reads in the cell; an empty cell shows the Add Note button instead, so this always has text. */
function NotePreview({ notes, label, preview }: { notes: string; label: string; preview: "expandable" | "short" }) {
  if (preview === "short") {
    // A fixed-width slot: the column never changes width, so typing a note never reflows the table.
    return (
      <span className="inline-block w-44 shrink-0 truncate text-gray-700" title={notes}>
        {truncate(notes, SHORT_NOTE_PREVIEW_CHARACTERS)}
      </span>
    );
  }
  return <ExpandableNote text={notes} label={label} />;
}
